import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '@/integrations/supabase/client';
import type { AppRole } from '@/types/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  role: AppRole | null;
  isAdmin: boolean;
  isConfigured: boolean;
  hasOnboarded: boolean;
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const isConfigured = isSupabaseConfigured();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [role, setRole] = useState<AppRole | null>(null);
  const [hasOnboarded, setHasOnboarded] = useState(false);

  const fetchUserRole = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user role:', error);
        return null;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data as any)?.role as AppRole | null;
    } catch (err) {
      console.error('Error in fetchUserRole:', err);
      return null;
    }
  }, []);

  const fetchHasOnboarded = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('promo_codes')
        .select('code')
        .eq('owner_user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching promo code:', error);
        return false;
      }

      // If code is empty or null, user hasn't onboarded yet
      const code = (data as any)?.code ?? '';
      return code.length > 0;
    } catch (err) {
      console.error('Error in fetchHasOnboarded:', err);
      return false;
    }
  }, []);

  const loadUserData = useCallback(async (userId: string) => {
    try {
      const [userRole, onboarded] = await Promise.all([
        fetchUserRole(userId),
        fetchHasOnboarded(userId),
      ]);
      setRole(userRole);
      setHasOnboarded(onboarded);
    } catch (err) {
      console.error('Failed to load user data:', err);
      setRole(null);
      setHasOnboarded(false);
    } finally {
      setIsLoading(false);
    }
  }, [fetchUserRole, fetchHasOnboarded]);

  useEffect(() => {
    if (!isConfigured) {
      setIsLoading(false);
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      try {
        console.log('Auth state change:', event, session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          setIsLoading(true);
          loadUserData(session.user.id);
        } else {
          setRole(null);
          setHasOnboarded(false);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error in onAuthStateChange:', err);
        setIsLoading(false);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        setIsLoading(true);
        loadUserData(session.user.id);
      } else {
        setIsLoading(false);
      }
    }).catch((err) => {
      console.error('Failed to get session:', err);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [loadUserData, isConfigured]);

  const signInWithMagicLink = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/confirm`,
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRole(null);
    setHasOnboarded(false);
  };

  const value = {
    user,
    session,
    isLoading,
    role,
    isAdmin: role === 'admin',
    isConfigured,
    hasOnboarded,
    signInWithMagicLink,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
